/**
 * Вкладка «Оплата» карточки сделки: все оплаты — статус, способ, касса, кто
 * добавил/подтвердил/отменил, чеки ОФД — и итоги. Как `paymentList` в CRM.
 * Спека `docs/specs/2026-09-25-deal-contract-payments-design.md`; те же данные —
 * `prokat-runner deal-payments ID`.
 */
import { useQuery } from "@tanstack/react-query";
import { ReceiptText, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

import { type DealPayments, type Payment, loadDealPayments } from "@/lib/dealPayments";
import { NavMenu } from "@/nav/NavMenu";

const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;
const dt = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "—");

function Stat({ label, value, hint, tone = "" }: { label: string; value: ReactNode; hint?: string; tone?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3" data-annot={`оплата «${label}»`}>
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className={`text-[20px] font-semibold leading-none tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="text-[11.5px] text-fg-faint">{hint}</div>}
    </div>
  );
}

function Row({ p }: { p: Payment }) {
  const off = !!p.cancelled;
  return (
    <tr className={`border-t border-border align-top ${off ? "text-fg-muted" : ""}`} data-annot={`оплата ${p.id}`}>
      <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-fg-muted">{dt(p.at)}</td>
      <td className={`whitespace-nowrap px-3 py-1.5 text-right font-medium tabular-nums ${p.paid ? "text-ok" : off ? "line-through" : ""}`}>
        {rub(p.amount)}
      </td>
      <td className="px-3 py-1.5">
        <div className="font-medium" style={p.status_color ? { color: p.status_color } : undefined}>
          {p.status ?? "—"}
        </div>
        {p.category && <div className="text-[11.5px] text-fg-muted">{p.category}</div>}
      </td>
      <td className="px-3 py-1.5">
        <div>{p.sys ?? <span className="text-err">нет способа</span>}</div>
        <div className="text-[11.5px] text-fg-muted">{[p.kassa, p.city].filter(Boolean).join(" · ") || "касса не указана"}</div>
        {p.email && <div className="text-[11.5px] text-fg-faint">чек на {p.email}</div>}
      </td>
      <td className="px-3 py-1.5 text-[12px]">
        <div>добавил {p.who ?? "—"}</div>
        {p.confirmed && (
          <div className="text-ok">
            подтвердил {p.confirmed.who ?? "—"} · {dt(p.confirmed.at)}
          </div>
        )}
        {p.cancelled && (
          <div className="text-err">
            отменил {p.cancelled.who ?? "—"} · {dt(p.cancelled.at)}
            {p.cancelled.comment && <> · {p.cancelled.comment}</>}
          </div>
        )}
      </td>
      <td className="px-3 py-1.5 text-[12px]">
        {p.receipts.length ? (
          p.receipts.map((r, i) => (
            <div key={i} className="flex items-center gap-1">
              <ReceiptText size={11} aria-hidden className="text-fg-faint" />
              {r.url ? (
                <a href={r.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                  {r.label}
                </a>
              ) : (
                <span>{r.label}</span>
              )}
              {r.amount !== null && <span className={`tabular-nums ${r.amount < 0 ? "text-err" : "text-fg-muted"}`}>{rub(r.amount)}</span>}
              <span className="text-fg-faint">{dt(r.at)}</span>
            </div>
          ))
        ) : (
          <span className="text-fg-faint">чека нет</span>
        )}
      </td>
      <td className="px-3 py-1.5">
        <NavMenu r={{ obj: "paymentID", id: p.id }} />
      </td>
    </tr>
  );
}

function Body({ d }: { d: DealPayments }) {
  const t = d.totals;
  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Оплачено" value={rub(t.paid)} tone="text-ok" hint={`из ${rub(t.contract)} по договору`} />
        <Stat label="К оплате" value={rub(t.due)} tone={t.due > 0 ? "text-warn" : ""} />
        <Stat label="Ждём оплату" value={rub(t.waiting)} hint="«Ждем Оплату» и «Подтвердить»" />
        <Stat label="Отменено / возвраты" value={`${rub(t.cancelled)} / ${rub(t.returned)}`} tone={t.returned ? "text-err" : ""} />
      </div>
      {t.mismatch && (
        <div className="flex items-center gap-2 text-[12.5px] font-medium text-err" data-annot="оплата расхождение">
          <TriangleAlert size={12} aria-hidden />В сделке CRM оплачено {rub(t.deal_paid)} — не сходится с оплатами ({rub(t.paid)}).
        </div>
      )}
      {d.payments.length ? (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
          <table className="w-full text-[12.5px]" data-annot="оплаты сделки">
            <thead className="text-left text-[12px] text-fg-muted">
              <tr>
                <th className="px-3 py-1 font-normal">Дата</th>
                <th className="px-3 py-1 text-right font-normal">Сумма</th>
                <th className="px-3 py-1 font-normal">Статус</th>
                <th className="px-3 py-1 font-normal">Способ · касса</th>
                <th className="px-3 py-1 font-normal">Кто</th>
                <th className="px-3 py-1 font-normal">Чеки</th>
                <th className="px-3 py-1 font-normal" />
              </tr>
            </thead>
            <tbody>
              {d.payments.map((p) => (
                <Row key={p.id} p={p} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-[12.5px] text-fg-muted">Оплат по сделке нет.</div>
      )}
    </>
  );
}

export function DealPaymentsTab({ dealId }: { dealId: number }) {
  const q = useQuery({ queryKey: ["deal-payments", dealId], queryFn: () => loadDealPayments(dealId) });
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Загружаю оплаты из CRM…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить оплаты: {String(q.error.message)}</div>;
  return <Body d={q.data} />;
}
